<style scoped>
#worklist {
	padding: 0px;
}
</style>

<template>
	<v-container id="worklist" fluid>
		<v-row no-gutters>
			<v-col cols="12" class="toolbar">
				<v-btn small class="d-md-none mr-1 text-capitalize" @click="on_back" v-if="show_result">Show search</v-btn>
				<v-btn small class="mr-1 text-capitalize" @click="toggle_user_show_search" v-if="window_width >= 960 && !user_show_search">Show search</v-btn>
				<v-btn small class="mr-1 text-capitalize" @click="toggle_user_show_search" v-if="window_width >= 960 && user_show_search">Hide search</v-btn>
				<v-btn small @click="save()" class="mr-1 text-capitalize" v-if="worklist.is_owned && !saving">Save</v-btn>

				<v-progress-circular
					indeterminate
					color="primary"
					v-if="saving"
				/>

				<v-btn small @click="save_as()" class="mr-1 text-capitalize">Save as</v-btn>
				<v-btn small @click="new_search()" class="mr-1 text-capitalize">New search</v-btn>
			</v-col>
			<v-col cols="12" style="padding-left: 10px" class="white pb-2">
				<v-alert type="error" v-if="errormsg != ''">{{
					errormsg
				}}</v-alert>

				<v-worklist-header
					:worklist="worklist"
					v-if="!loading"
					@changed_share="acl => changed_shared(acl)"
				/>

				<v-progress-circular
					indeterminate
					color="primary"
					v-if="loading"
				/>
			</v-col>
			<v-col cols="12" md="4" lg="3" v-if="show_search">
				<v-search-filter :search="search" @on_search="do_search()" ref="search" />
			</v-col>
			<v-col cols="12" :md="user_show_search ? 8 : 12" :lg="user_show_search ? 9 : 12" v-if="show_result">
				<v-search-results
					@click_abook="abook => open_abook(abook)"

					:show_columns="search.show_columns"
					@columns_changed="new_columns => search.show_columns = new_columns"

					:can_sort_columns="true"
					:sort_columns="search.sort_columns"
					@sort_changed="new_sort => search.sort_columns = new_sort"

					ref="results" 
				/>
			</v-col>
		</v-row>

		<v-new-worklist-dialog
			@saved="worklist => saved_worklist(worklist)"
			ref="new_worklist_dialog"
		/>
	</v-container>
</template>

<script>
// note, we use a similar setup from SearchJobs here as this is very close to it but with the focus on the results being shown.
import search_filter from "@/components/addressbook/search_filter";
import search_results from "@/components/addressbook/search_results";
import new_worklist_dialog from "@/components/worklist/new_worklist_dialog";
import worklist_header from "@/components/worklist/worklist_header";
import { searchMixin } from "@/mixins/addressbook/searchMixin";
import Worklist from "@/models/worklist.js";

export default {
	mixins: [ searchMixin ],
	data() {
		return {
			errormsg: "",
			loading: false,
			saving: false,
			worklist: new Worklist(),
			added_crumb: false,
			window_width: 0,
			mode: 0, /* 0 = search, 1 = results */
			user_show_search: false, /* in wide mode the user can show/hide search */
			show_search: true,
			show_result: true
		}
	},
	components: {
		'v-search-filter': search_filter,
		'v-search-results': search_results,
		'v-new-worklist-dialog': new_worklist_dialog,
		'v-worklist-header': worklist_header,
	},
	created() {
		// we do not update the store here
		this.load(this.$route.params.id)
	},
	mounted() {
		this.$nextTick(() => {
			window.addEventListener('resize', this.on_resize);

			// TODO need to check if we've stored search settings and if so re-execute them

			// call resize so we know our window size and apply our mode
			this.on_resize();
		})
	},
	beforeDestroy() { 
		window.removeEventListener('resize', this.on_resize); 
	},
	watch: {
		'$route.params.id': function(id) {
			this.$nextTick(() => {
				this.added_crumb = false;
				this.load(id);
			});
		}
	},
	methods: {
		set_mode(new_mode) {
			this.mode = new_mode;

			this.show_search = (this.mode == 0) || (this.window_width >= 960 && this.user_show_search);
			this.show_result = (this.mode == 1) || (this.window_width >= 960);
		},
		toggle_user_show_search() {
			this.user_show_search = !this.user_show_search;
			this.show_search = (this.mode == 0) || (this.window_width >= 960 && this.user_show_search);
		},
		load(wl_id) {
			let self = this; // make sure we can access this inside of our promisses
			this.loading = true;

			this.worklist.load(wl_id)
				.then(function() {
					// copy our search info into a clean search object
					self.parse_search(self.worklist.settings);

					// make sure we default to our results view
					self.set_mode(1);

					// if this wasn't visible yet (mobile) it will be next tick
					self.$nextTick(() => {
						if (self.search.show_columns.length == 0) {
							self.search.show_columns = self.$refs.results.default_columns()
						}
						self.$refs.results.perform_search(self.search);
					});

					// need to add support for non-dynamic worklists

					// we add this the first time around
					if (!self.added_crumb) {
						self.added_crumb = true;

						self.$store.commit("push_breadcrumb", {
							exact: true,
							text: self.worklist.name,
							to: self.$route.path
						});
					};	
				})
				.catch(function(message) {
					// show our error message
					self.errormsg = message;					
				})
				.finally(function() {
					self.loading = false;
				});
		},
		save() {
			// we'll just save the worklist with our updated search
			this.worklist.settings = { ...this.search };

			// and save
			this.saving = true;
			this.errormsg = "";

			this.worklist.save()
				.then(() => {
					// all good
				})
				.catch(reason => {
					// show our error message
					this.errormsg = reason;
				})
				.finally(() => {
					// no longer saving
					this.saving = false;
				});
		},
		save_as() {
			// open our save dialog so the user can provide a new name
			let wl = {
				class: "restful.addressbook.worklists",
				name: this.worklist.name,
				settings: { ...this.search },
				is_dynamic: true,
				details: []
			};

			// open up save dialog
			this.$refs.new_worklist_dialog.open(wl);
		},
		saved_worklist(worklist) {
			this.$router.push({ name: "Abook Worklist", params: { id:  worklist.id }});
		},
		new_search() {
			this.$router.push({ name: "Search Addressbook" })
		},
		do_search() {
			this.set_mode(1);

			// TODO need to store these search settings somewhere so we return to it

			this.$nextTick(() => {
				this.$refs.results.perform_search(this.search);
			})
		},
		on_back() {
			this.set_mode(0);
		},
		open_abook(abook) {
			this.$router.push({ name: "View Addressbook", params: { id: abook.id }} );
		},
		changed_shared(acl) {
			// we just update our data
			this.worklist.acl = acl;
		},
		on_resize() {
			this.window_width = window.innerWidth;
			this.set_mode(this.mode)
		}
	}
}
</script>
